import { Subject } from 'rxjs';
export class ChatInstance {
    constructor() {
        this.currentMsgId = 0;
        this.sentMessages = new Map();
        this.connectionTimeoutFallback = 10000;
        this.reconnectionTimeoutFallback = 300000;
        this.reconnectionIntervalFallback = 5000;
        this.sendMessageTimeoutFallback = 5000;
        this.closureTimeoutFallback = 10000;
        this.isTypingFallback = 5000;
        this.pingFallback = 5000;
        this.pingLimitFallback = 5;
        this.onStatusChange$ = new Subject();
        this.onFeatureUpdate$ = new Subject();
        this.onParticipantUpdate$ = new Subject();
        this.onIsTyping$ = new Subject();
        this.onNewMessage$ = new Subject();
        this.onError$ = new Subject();
        this.onNotification$ = new Subject();
    }
    observableEvents() {
        return {
            onStatusChange$: this.onStatusChange$.asObservable(),
            onFeatureUpdate$: this.onFeatureUpdate$.asObservable(),
            onParticipantUpdate$: this.onParticipantUpdate$.asObservable(),
            onIsTyping$: this.onIsTyping$.asObservable(),
            onNewMessage$: this.onNewMessage$.asObservable(),
            onError$: this.onError$.asObservable(),
            onNotification$: this.onNotification$.asObservable(),
        };
    }
    emitError(code, message) {
        this.onError$.next({
            code,
            message,
        });
    }
    emitStatusChange(sessionId, provider, connection, chat) {
        this.chatStatus = chat;
        this.connectionState = connection;
        this.onStatusChange$.next({
            sessionId,
            provider,
            connection,
            chat,
            timestamp: new Date(),
        });
    }
    emitIsTyping(participant, timestamp) {
        this.onIsTyping$.next({
            participant,
            isTyping: true,
            timestamp,
        });
    }
    emitNewMessage(provider, participant, messages, timestamp) {
        this.onNewMessage$.next({
            messages,
            provider,
            participant,
            timestamp,
        });
    }
    emitFeatureUpdate(features, settings) {
        this.chatFeatures = features;
        this.chatSettings = settings;
        this.onFeatureUpdate$.next(features);
    }
    emitParticipantUpdate(provider, participant, action, status, timestamp) {
        this.onParticipantUpdate$.next({
            participant,
            provider,
            action,
            status,
            timestamp,
        });
    }
    emitNotification(notificationType, timestamp, url, label, content, secondsRemaining) {
        this.onNotification$.next({
            notificationType,
            url,
            label,
            content,
            secondsRemaining,
            timestamp,
        });
    }
    getCurrentConnectionState() {
        return this.connectionState;
    }
    getCurrentChatState() {
        return this.chatStatus;
    }
    getCurrentStates() {
        return [this.getCurrentConnectionState(), this.getCurrentChatState()];
    }
    getChatFeatures() {
        return this.chatFeatures;
    }
    getChatSettings() {
        return this.chatSettings;
    }
    getPlatformSettings() {
        return this.platformSettings;
    }
    parseIntOrDefault(value, defaultValue = undefined) {
        const parsedValue = parseInt(value);
        return isNaN(parsedValue) ? defaultValue : parsedValue;
    }
    setPlatformSettings(settings) {
        var _a;
        this.platformSettings = {
            chatUrl: (_a = settings === null || settings === void 0 ? void 0 : settings.chatUrl) !== null && _a !== void 0 ? _a : undefined,
            connectionTimeout: this.parseIntOrDefault(settings === null || settings === void 0 ? void 0 : settings.connectionTimeout),
            reconnectionTimeout: this.parseIntOrDefault(settings === null || settings === void 0 ? void 0 : settings.reconnectionTimeout),
            pingInterval: this.parseIntOrDefault(settings === null || settings === void 0 ? void 0 : settings.pingInterval),
            pingRetryLimit: this.parseIntOrDefault(settings === null || settings === void 0 ? void 0 : settings.pingRetryLimit),
            acknowledgementTimeout: this.parseIntOrDefault(settings === null || settings === void 0 ? void 0 : settings.acknowledgementTimeout),
            closeChatTimeout: this.parseIntOrDefault(settings === null || settings === void 0 ? void 0 : settings.closeChatTimeout),
        };
    }
    getChatUrl() {
        var _a, _b;
        return (_b = (_a = this.getPlatformSettings()) === null || _a === void 0 ? void 0 : _a.chatUrl) !== null && _b !== void 0 ? _b : undefined;
    }
    getConnectionTimeout() {
        var _a, _b;
        return (_b = (_a = this.getPlatformSettings()) === null || _a === void 0 ? void 0 : _a.connectionTimeout) !== null && _b !== void 0 ? _b : this.connectionTimeoutFallback;
    }
    getReconnectionTimeout() {
        var _a, _b, _c, _d;
        if ((_a = this.getChatSettings()) === null || _a === void 0 ? void 0 : _a.reconnectionTimeout) {
            return ((_b = this.getChatSettings()) === null || _b === void 0 ? void 0 : _b.reconnectionTimeout) * 1000;
        }
        if ((_c = this.getPlatformSettings()) === null || _c === void 0 ? void 0 : _c.reconnectionTimeout) {
            return ((_d = this.getPlatformSettings()) === null || _d === void 0 ? void 0 : _d.reconnectionTimeout) * 1000;
        }
        return this.reconnectionTimeoutFallback;
    }
    getReconnectionInterval() {
        var _a, _b;
        if ((_a = this.getChatSettings()) === null || _a === void 0 ? void 0 : _a.reconnectionInterval) {
            return ((_b = this.getChatSettings()) === null || _b === void 0 ? void 0 : _b.reconnectionInterval) * 1000;
        }
        return this.reconnectionIntervalFallback;
    }
    getPingInterval() {
        var _a, _b, _c, _d;
        return (_d = (_b = (_a = this.getChatSettings()) === null || _a === void 0 ? void 0 : _a.pingInterval) !== null && _b !== void 0 ? _b : (_c = this.getPlatformSettings()) === null || _c === void 0 ? void 0 : _c.pingInterval) !== null && _d !== void 0 ? _d : this.pingFallback;
    }
    getPingLimit() {
        var _a, _b, _c, _d;
        return (_d = (_b = (_a = this.getChatSettings()) === null || _a === void 0 ? void 0 : _a.pingRetryLimit) !== null && _b !== void 0 ? _b : (_c = this.getPlatformSettings()) === null || _c === void 0 ? void 0 : _c.pingRetryLimit) !== null && _d !== void 0 ? _d : this.pingLimitFallback;
    }
    getAcknowledgementTimeout() {
        var _a, _b;
        return (_b = (_a = this.getPlatformSettings()) === null || _a === void 0 ? void 0 : _a.acknowledgementTimeout) !== null && _b !== void 0 ? _b : this.sendMessageTimeoutFallback;
    }
    getCloseChatTimeout() {
        var _a, _b;
        return (_b = (_a = this.getPlatformSettings()) === null || _a === void 0 ? void 0 : _a.closeChatTimeout) !== null && _b !== void 0 ? _b : this.closureTimeoutFallback;
    }
    getIsTypingInterval() {
        var _a, _b;
        return (_b = (_a = this.getChatSettings()) === null || _a === void 0 ? void 0 : _a.isTypingInterval) !== null && _b !== void 0 ? _b : this.isTypingFallback;
    }
    getNextMessageId() {
        return this.currentMsgId++;
    }
    addToSentMessages(id, data) {
        this.sentMessages.set(id, {
            data,
            ackInfo: 'NOT DELIVERED',
            sendTries: 0,
        });
    }
    getSentMessages() {
        return this.sentMessages;
    }
}
//# sourceMappingURL=chat-instance.js.map